import { PropsWithChildren, useContext, useId, useLayoutEffect, useMemo, useRef } from 'react'

import { PortalContext } from 'core-components/library/popup'
import { getRoot } from 'core-components/library/popup/wrapper/helpers/getRoot'

import styles from './Partner360FontProvider.module.scss'

/** Own a font scope for this instance only: dialogs must not reuse an older window's
 * portal container, which would put them behind subsequently opened modal overlays. */
export const Partner360FontProvider = ({ children }: PropsWithChildren) => {
  const parentContext = useContext(PortalContext)
  const id = useId()
  const subAppClass = `p360-font-${id.replace(
    /[^a-zA-Z0-9_-]/g,
    (character) => `_${character.charCodeAt(0).toString(16)}_`
  )}`
  const ownedScope = useRef<{ parent: Element; element: Element } | null>(null)
  const { rootNode, containerParent } = parentContext
  const context = useMemo(() => ({ ...parentContext, subAppClass }), [parentContext, subAppClass])

  useLayoutEffect(() => {
    const popupRoot = getRoot({ rootNode, containerParent })
    // Keep the same owned node during StrictMode's effect replay, not across dialog instances.
    const scope =
      ownedScope.current?.parent === popupRoot
        ? ownedScope.current.element
        : getRoot({ rootNode, containerParent, subAppClass })
    scope.classList.add(styles.portalRoot)
    if (scope.parentElement !== popupRoot) {
      popupRoot.appendChild(scope)
    }
    ownedScope.current = { parent: popupRoot, element: scope }

    return () => scope.remove()
  }, [rootNode, containerParent, subAppClass])

  return <PortalContext.Provider value={context}>{children}</PortalContext.Provider>
}
